import { Directive, OnDestroy, OnInit } from '@angular/core';
import { SignalRService } from '../services/signalr.service';
import { HubConnection } from '@microsoft/signalr';

/**
 * Base component for managing SignalR connections and subscriptions
 * Provides automatic cleanup and device subscription management
 */
@Directive() 
export abstract class SignalRBaseComponent implements OnInit, OnDestroy {
  protected readonly notificationPath = '/notification';
  protected connection: HubConnection | undefined;
  protected isDestroyed = false;
  
  // Track registered handlers for automatic cleanup
  private registeredHandlers = new Map<string, (...args: any[]) => void>();
  
  // Track device subscriptions for automatic cleanup
  private deviceSubscriptions = new Set<string>();

  constructor(protected signalRService: SignalRService) {}

  async ngOnInit(): Promise<void> {
    try {
      this.connection = await this.signalRService.startNewConnection(this.notificationPath);
      
      if (!this.isDestroyed) {
        await this.registerHandlers();
      }
    } catch (error) {
      console.error('[SignalRBase] Failed to initialize SignalR:', error);
    }
  }

  /**
   * Implement this in your child component to register specific events
   * This is called after the connection is established
   */
  protected abstract registerHandlers(): Promise<void> | void;

  /**
   * Register a SignalR event handler
   * Automatically tracks the handler for cleanup
   * 
   * @param eventName - The SignalR event name to listen for
   * @param callback - The callback function to execute when event is received
   */
  protected registerSignalREvent(eventName: string, callback: (...args: any[]) => void): void {
    if (this.isDestroyed) {
      return;
    }

    this.registeredHandlers.set(eventName, callback);
    this.signalRService.registerHandler(this.notificationPath, eventName, callback);
  }

  /**
   * Subscribe to device-specific updates
   * Automatically tracks subscription for cleanup
   * 
   * @param deviceNumber - Device identifier to subscribe to
   */
  protected async subscribeToDevice(deviceNumber: string): Promise<void> {
    if (this.isDestroyed || !deviceNumber) {
      return;
    }

    try {
      await this.signalRService.subscribeToDevice(this.notificationPath, deviceNumber);
      this.deviceSubscriptions.add(deviceNumber);
    } catch (error) {
      console.error(`[SignalRBase] Failed to subscribe to device ${deviceNumber}:`, error);
    }
  }

  /**
   * Unsubscribe from device-specific updates
   * Removes from tracking
   * 
   * @param deviceNumber - Device identifier to unsubscribe from
   */
  protected async unsubscribeFromDevice(deviceNumber: string): Promise<void> {
    if (!deviceNumber) {
      return;
    }

    try {
      await this.signalRService.unsubscribeFromDevice(this.notificationPath, deviceNumber);
      this.deviceSubscriptions.delete(deviceNumber);
    } catch (error) {
      console.error(`[SignalRBase] Failed to unsubscribe from device ${deviceNumber}:`, error);
    }
  }

  /**
   * Subscribe to multiple devices at once
   * 
   * @param deviceNumbers - Array of device identifiers
   */
  protected async subscribeToDevices(deviceNumbers: string[]): Promise<void> {
    if (this.isDestroyed) {
      return;
    }

    const subscriptionPromises = deviceNumbers.map(deviceNumber => 
      this.subscribeToDevice(deviceNumber)
    );

    await Promise.allSettled(subscriptionPromises);
  }

  /**
   * Unsubscribe from multiple devices at once
   * 
   * @param deviceNumbers - Array of device identifiers
   */
  protected async unsubscribeFromDevices(deviceNumbers: string[]): Promise<void> {
    const unsubscriptionPromises = deviceNumbers.map(deviceNumber => 
      this.unsubscribeFromDevice(deviceNumber)
    );

    await Promise.allSettled(unsubscriptionPromises);
  }

  /**
   * Replace current device subscriptions with a new set
   * 
   * @param newDeviceNumbers - New set of device identifiers to subscribe to
   */
  protected async replaceDeviceSubscriptions(newDeviceNumbers: string[]): Promise<void> {
    if (this.isDestroyed) {
      return;
    }

    const currentDevices = Array.from(this.deviceSubscriptions);
    const newDevicesSet = new Set(newDeviceNumbers);

    // Find devices to unsubscribe from (in current but not in new)
    const toUnsubscribe = currentDevices.filter(device => !newDevicesSet.has(device));
    
    // Find devices to subscribe to (in new but not in current)
    const toSubscribe = newDeviceNumbers.filter(device => !this.deviceSubscriptions.has(device));

    // Unsubscribe from old devices
    if (toUnsubscribe.length > 0) {
      await this.unsubscribeFromDevices(toUnsubscribe);
    }

    // Subscribe to new devices
    if (toSubscribe.length > 0) {
      await this.subscribeToDevices(toSubscribe);
    }
  }

  /**
   * Remove a specific event handler
   * 
   * @param eventName - The event name to remove the handler for
   */
  protected removeSignalREvent(eventName: string): void {
    const callback = this.registeredHandlers.get(eventName);
    if (callback) {
      this.signalRService.removeHandler(this.notificationPath, eventName);
      this.registeredHandlers.delete(eventName);
    }
  }

  async ngOnDestroy(): Promise<void> {
    this.isDestroyed = true;

    try {
      // Unsubscribe from all devices
      const devices = Array.from(this.deviceSubscriptions);
      if (devices.length > 0) {
        await this.unsubscribeFromDevices(devices);
      }

      // Remove all event handlers
      this.registeredHandlers.forEach((callback, eventName) => {
        this.signalRService.removeHandler(this.notificationPath, eventName);
      });
      
      // Clear tracking maps
      this.registeredHandlers.clear();
      this.deviceSubscriptions.clear();
    } catch (error) {
      console.error('[SignalRBase] Error during cleanup:', error);
    }
  }

  /**
   * Check if currently subscribed to a device
   */
  protected isSubscribedToDevice(deviceNumber: string): boolean {
    return this.deviceSubscriptions.has(deviceNumber);
  }

  /**
   * Get all currently subscribed device numbers
   */
  protected getSubscribedDevices(): string[] {
    return Array.from(this.deviceSubscriptions);
  }
}
